import React from "react";
import { useNavigate } from "react-router-dom";
import slugify from "slugify";
import S from "../../../../assets/stylesheets/experience/sections/ExperienceSidebar.module.css";

export default function ExperienceSidebar({ experience, farmWebUrl, farmId, farmName }) {
  const navigate = useNavigate();
  const dates = experience?.startDates || experience?.availableDates || [];

  const isSampleFarm = String(farmId ?? "").startsWith("sample-");

  const formatDate = (date) =>
    typeof date === "string"
      ? new Date(date).toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" })
      : date?.startDate
      ? new Date(date.startDate).toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" })
      : String(date);

  const handleVisitFarm = () => {
    if (isSampleFarm) return;
    if (farmId) {
      const slug = slugify(farmName || "", { lower: true, strict: true });
      navigate(`/farm/${farmId}/${slug}`);
      return;
    }
    if (farmWebUrl) {
      const href = farmWebUrl.startsWith("http") ? farmWebUrl : `https://${farmWebUrl}`;
      window.open(href, "_blank", "noopener,noreferrer");
    }
  };

  const showVisitBtn = Boolean(farmId || farmWebUrl);
  const farmHref = null; // replaced by handleVisitFarm

  return (
    <>
      {/* ── Desktop sidebar ── */}
      <aside className={S["exp-sidebar"]}>
        <div className={S["sidebar-card"]}>

          <div className={S["sidebar-price-btn-row"]}>
            {experience?.price != null ? (
              <div className={S["sidebar-price-row"]}>
                <span className={S["sidebar-price"]}>From ${experience.price} CAD</span>
                <span className={S["sidebar-price-label"]}>/ guest</span>
              </div>
            ) : <span />}

            {showVisitBtn && (
              <button onClick={handleVisitFarm} className={S["sidebar-link-btn"]} style={isSampleFarm ? { opacity: 0.45, cursor: "default" } : undefined}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
                  <polyline points="15 3 21 3 21 9" />
                  <line x1="10" y1="14" x2="21" y2="3" />
                </svg>
                Visit Farm
              </button>
            )}
          </div>

          {(experience?.duration || experience?.groupSize) && (
            <hr className={S["sidebar-divider"]} />
          )}

          {experience?.duration && (
            <div className={S["sidebar-info-row"]}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" />
              </svg>
              {experience.duration}
            </div>
          )}
          {experience?.groupSize && (
            <div className={S["sidebar-info-row"]}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" />
              </svg>
              Up to {experience.groupSize} guests
            </div>
          )}

          {experience?.startAt && (
            <>
              <hr className={S["sidebar-divider"]} />
              <div className={S["sidebar-date-card"]}>
                <div className={S["sidebar-date-card-left"]}>
                  <span className={S["sidebar-date-card-month"]}>
                    {new Date(experience.startAt).toLocaleDateString(undefined, { month: "short" }).toUpperCase()}
                  </span>
                  <span className={S["sidebar-date-card-day"]}>
                    {new Date(experience.startAt).getDate()}
                  </span>
                </div>
                <div className={S["sidebar-date-card-right"]}>
                  <span className={S["sidebar-date-card-weekday"]}>
                    {new Date(experience.startAt).toLocaleDateString(undefined, { weekday: "long" })}
                  </span>
                  <span className={S["sidebar-date-card-full"]}>
                    {new Date(experience.startAt).toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" })}
                  </span>
                  {experience?.endAt && (
                    <span className={S["sidebar-date-card-time"]}>
                      {new Date(experience.startAt).toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" })}
                      {" – "}
                      {new Date(experience.endAt).toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" })}
                    </span>
                  )}
                </div>
              </div>
            </>
          )}

          {dates.length > 0 && (
            <>
              <hr className={S["sidebar-divider"]} />
              <p className={S["sidebar-title"]}>Available dates</p>
              <ul className={S["sidebar-date-list"]}>
                {dates.slice(0, 5).map((date, idx) => (
                  <li key={idx} className={S["sidebar-date-item"]}>
                    <svg className={S["sidebar-date-icon"]} width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                      <line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" />
                    </svg>
                    {formatDate(date)}
                  </li>
                ))}
              </ul>
            </>
          )}
        </div>
      </aside>

      {/* ── Mobile: sticky bottom bar ── */}
      <div className={S["mobile-bottom-bar"]}>
        {experience?.price != null && (
          <div className={S["mobile-bottom-price-row"]}>
            <span className={S["mobile-bottom-price"]}>From ${experience.price} CAD</span>
            <span className={S["mobile-bottom-price-label"]}>/ guest</span>
          </div>
        )}
        {showVisitBtn && (
          <button onClick={handleVisitFarm} className={S["mobile-bottom-btn"]} style={isSampleFarm ? { opacity: 0.45, cursor: "default" } : undefined}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
              <polyline points="15 3 21 3 21 9" />
              <line x1="10" y1="14" x2="21" y2="3" />
            </svg>
            Visit Farm
          </button>
        )}
      </div>

      {/* ── Mobile: available dates inline section ── */}
      {dates.length > 0 && (
        <div className={S["mobile-dates-section"]}>
          <p className={S["mobile-dates-title"]}>Available dates</p>
          <ul className={S["mobile-dates-list"]}>
            {dates.slice(0, 5).map((date, idx) => (
              <li key={idx} className={S["mobile-dates-item"]}>
                <svg className={S["mobile-dates-icon"]} width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                  <line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" />
                </svg>
                {formatDate(date)}
              </li>
            ))}
          </ul>
        </div>
      )}
    </>
  );
}
